import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { formatCents } from '@crave/shared';
import { AllergenList, Button, NutritionFactsLabel, Spicy, allergenLabels } from '@crave/ui';
import { cart } from '../lib/cart.ts';
import type { MenuNutrition } from '../lib/types.ts';
import { useMenu } from './MenuPage.tsx';

/* The disclosure row is styled here once: Safari draws its own triangle unless the marker is hidden. */
const CSS = `
.crave-info { border: 1px solid var(--line); border-radius: var(--radius); background: var(--paper); }
.crave-info > summary { display: flex; align-items: center; justify-content: space-between; gap: 12px;
  min-height: 52px; padding: 0 16px; font-weight: 700; cursor: pointer; list-style: none;
  border-radius: var(--radius); -webkit-tap-highlight-color: transparent; }
.crave-info > summary::-webkit-details-marker { display: none; }
.crave-info > summary::marker { content: ''; }
.crave-info > summary:focus-visible { outline: 3px solid var(--crave-red); outline-offset: 2px; }
.crave-info-chevron { flex: none; width: 10px; height: 10px; margin-right: 4px; border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor; transform: translateY(-3px) rotate(45deg); transition: transform 0.15s; }
.crave-info[open] .crave-info-chevron { transform: translateY(2px) rotate(-135deg); }
.crave-info-body { display: grid; gap: 12px; padding: 8px 16px 16px; min-width: 0; }
.crave-info-body > p { margin: 0; font-size: 13px; line-height: 1.45; color: var(--ink-soft); max-width: 460px; }
@media (prefers-reduced-motion: reduce) { .crave-info-chevron { transition: none; } }
`;

export function ItemPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const q = useMenu();
  const [selected, setSelected] = useState<string[]>([]);
  const [qty, setQty] = useState(1);
  const item = q.data?.categories.flatMap((c) => c.items).find((i) => i.slug === slug);
  if (q.isLoading) return <p>Loading…</p>;
  if (q.isError || !q.data)
    return (
      <p style={{ color: 'var(--crave-red)' }}>
        The menu is unavailable right now. Please try again shortly.
      </p>
    );
  if (!item) return <p>That pizza isn't on the menu.</p>;
  const closed = !q.data.location.ordering_enabled || q.data.location.ordering_paused;
  const allergenNotice = q.data.allergen_notice?.trim();
  const removes = item.modifiers.filter((m) => m.kind === 'REMOVE_INGREDIENT');
  const adds = item.modifiers.filter((m) => m.kind === 'ADD_INGREDIENT');
  // Spicy on its own, or spicy because an add-on was ticked (#65).
  const spicy = item.spicy === true || adds.some((m) => m.makes_spicy && selected.includes(m.id));
  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  // Hidden prices come back null: the cart then prices the order from the quote.
  const pricesShown = q.data.location.show_prices !== false && item.price_cents !== null;
  const modsCents = item.modifiers
    .filter((m) => selected.includes(m.id))
    .reduce((s, m) => s + (m.price_delta_cents ?? 0), 0);
  const unit = (item.price_cents ?? 0) + modsCents;
  const canOrder = item.orderable && !closed && item.availability !== 'sold_out';

  return (
    <div style={{ display: 'grid', gap: 20, minWidth: 0 }}>
      <style href="crave-order-item" precedence="default">
        {CSS}
      </style>
      <button
        onClick={() => nav(-1)}
        style={{
          background: 'none',
          border: 0,
          textAlign: 'left',
          color: 'var(--ink-soft)',
          cursor: 'pointer',
          justifySelf: 'start',
          minHeight: 'var(--touch)',
          padding: '0 12px 0 0',
          margin: '-10px 0',
        }}
      >
        ← Menu
      </button>
      {item.image_path && (
        <img
          src={`https://cravepizzafl.com/${item.image_path.replace(/\.jpg$/, '-1254.jpg')}`}
          alt={`${item.name} pizza`}
          style={{
            width: '100%',
            borderRadius: 'var(--radius)',
            aspectRatio: '1',
            objectFit: 'cover',
          }}
        />
      )}
      <div style={{ display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, fontSize: 28 }}>{item.name}</h1>
          {spicy && <Spicy />}
        </div>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>{item.ingredients.join(', ')}</p>
        {/* "None" is a food-safety claim: only made when every ingredient's allergens were checked. */}
        <AllergenList
          allergens={item.allergens}
          none={
            item.allergens_complete ? 'No major allergens in our recipe' : 'Ask us about allergens.'
          }
          style={{ fontSize: 14, color: 'var(--ink)' }}
        />
        {/* A list with unchecked ingredients may be missing an allergen, so it is not shown as complete. */}
        {!item.allergens_complete && allergenLabels(item.allergens).length > 0 && (
          <p style={{ margin: 0, fontSize: 14, color: 'var(--ink)' }}>
            Tell us about any allergy before you order.
          </p>
        )}
        {allergenNotice && (
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: 'var(--ink-soft)' }}>
            {allergenNotice}
          </p>
        )}
      </div>
      {removes.length > 0 && (
        <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 8 }}>
          <legend style={{ fontWeight: 700, marginBottom: 8 }}>Leave off</legend>
          {removes.map((m) => (
            <Check
              key={m.id}
              label={m.name}
              checked={selected.includes(m.id)}
              onChange={() => toggle(m.id)}
            />
          ))}
        </fieldset>
      )}
      {adds.length > 0 && (
        <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 8 }}>
          <legend style={{ fontWeight: 700, marginBottom: 8 }}>Add</legend>
          {adds.map((m) => (
            <Check
              key={m.id}
              label={
                <>
                  {pricesShown && m.price_delta_cents !== null
                    ? `${m.name} · +${formatCents(m.price_delta_cents)}`
                    : m.name}
                  {m.makes_spicy && (
                    <>
                      {' '}
                      <Spicy size="sm" />
                    </>
                  )}
                </>
              }
              checked={selected.includes(m.id)}
              onChange={() => toggle(m.id)}
            />
          ))}
        </fieldset>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontWeight: 700 }}>Quantity</span>
        <Button
          variant="secondary"
          aria-label="Fewer"
          onClick={() => setQty((n) => Math.max(1, n - 1))}
        >
          −
        </Button>
        <span style={{ minWidth: 24, textAlign: 'center', fontWeight: 700 }}>{qty}</span>
        <Button
          variant="secondary"
          aria-label="More"
          onClick={() => setQty((n) => Math.min(10, n + 1))}
        >
          +
        </Button>
      </div>
      <Button
        size="lg"
        disabled={!canOrder}
        onClick={() => {
          cart.add({
            menu_item_id: item.id,
            slug: item.slug,
            name: item.name,
            quantity: qty,
            modifier_ids: selected,
            modifier_names: item.modifiers
              .filter((m) => selected.includes(m.id))
              .map((m) => m.name),
            spicy,
            unit_price_cents: item.price_cents ?? 0,
            modifiers_cents: modsCents,
          });
          void nav('/cart');
        }}
      >
        {canOrder
          ? pricesShown
            ? `Add ${qty} to cart · ${formatCents(unit * qty)}`
            : `Add ${qty} to cart`
          : item.availability === 'sold_out'
            ? 'Sold out'
            : closed
              ? 'Ordering opens soon'
              : 'Not available'}
      </Button>
      {item.nutrition && <NutritionDetails nutrition={item.nutrition} />}
    </div>
  );
}

/** "Nutrition facts" disclosure: one slice and the whole pizza, side by side. */
function NutritionDetails({ nutrition }: { nutrition: MenuNutrition }) {
  const slices = Math.max(1, Math.round(nutrition.servings));
  const grams = Math.round(nutrition.grams_per_serving);
  return (
    <details className="crave-info">
      <summary>
        <span>Nutrition facts</span>
        <span className="crave-info-chevron" aria-hidden="true" />
      </summary>
      <div className="crave-info-body">
        <NutritionFactsLabel
          perServing={nutrition.per_serving}
          perContainer={nutrition.per_item}
          servingSize={grams > 0 ? `1 slice (${grams}g)` : '1 slice'}
          servingsPerContainer={slices}
          servingsText={`${slices} ${slices === 1 ? 'slice' : 'slices'} per pizza`}
          showPerContainer
          servingColumnLabel="Per slice"
          containerColumnLabel="Per pizza"
          headingLevel={2}
        />
        <p>
          Values are calculated from our recipe and standard ingredient data. Numbers are for the
          pizza as listed. Leaving off or adding toppings changes them.
        </p>
      </div>
    </details>
  );
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minHeight: 44,
        padding: '0 12px',
        border: '2px solid var(--line)',
        borderRadius: 8,
        background: checked ? 'var(--mist)' : 'var(--paper)',
        cursor: 'pointer',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        style={{ width: 20, height: 20 }}
      />
      <span>{label}</span>
    </label>
  );
}
